import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Upload, FileText } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface DiffLine {
  type: 'added' | 'removed' | 'unchanged';
  content: string;
  lineNumber?: number;
}

export const TextDiffTool: React.FC = () => {
  const { t } = useTranslation();
  const [originalText, setOriginalText] = useState('');
  const [modifiedText, setModifiedText] = useState('');

  const diffLines = useMemo(() => {
    if (!originalText && !modifiedText) return [];

    const originalLines = originalText.split('\n');
    const modifiedLines = modifiedText.split('\n');
    const result: DiffLine[] = [];

    // Simple diff algorithm
    const maxLines = Math.max(originalLines.length, modifiedLines.length);
    
    for (let i = 0; i < maxLines; i++) {
      const originalLine = originalLines[i] || '';
      const modifiedLine = modifiedLines[i] || '';

      if (originalLine === modifiedLine) {
        result.push({
          type: 'unchanged',
          content: originalLine,
          lineNumber: i + 1
        });
      } else {
        if (originalLines[i] !== undefined) {
          result.push({
            type: 'removed',
            content: originalLine,
            lineNumber: i + 1
          });
        }
        if (modifiedLines[i] !== undefined) {
          result.push({
            type: 'added',
            content: modifiedLine,
            lineNumber: i + 1
          });
        }
      }
    }

    return result;
  }, [originalText, modifiedText]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, isOriginal: boolean) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (isOriginal) {
          setOriginalText(content);
        } else {
          setModifiedText(content);
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = () => {
    setOriginalText('');
    setModifiedText('');
  };

  const stats = {
    added: diffLines.filter(line => line.type === 'added').length,
    removed: diffLines.filter(line => line.type === 'removed').length,
    unchanged: diffLines.filter(line => line.type === 'unchanged').length,
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>{t('tools.textDiff.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button onClick={clearAll} variant="outline">
              {t('tools.textDiff.clearAll')}
            </Button>
            <Button
              onClick={() => copyToClipboard(diffLines.map(line =>
                `${line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '} ${line.content}`
              ).join('\n'))}
              disabled={diffLines.length === 0}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('tools.textDiff.copyDiff')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Input Texts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.textDiff.original')}</span>
              <div>
                <input
                  type="file"
                  accept=".txt,.md,.json,.xml,.csv"
                  onChange={(e) => handleFileUpload(e, true)}
                  className="hidden"
                  id="original-file"
                />
                <Button
                  onClick={() => document.getElementById('original-file')?.click()}
                  size="sm"
                  variant="outline"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  {t('common.upload')}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              placeholder={t('tools.textDiff.enterOriginalText')}
              className="min-h-[300px] font-mono text-sm"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t('tools.textDiff.modified')}</span>
              <div>
                <input
                  type="file"
                  accept=".txt,.md,.json,.xml,.csv"
                  onChange={(e) => handleFileUpload(e, false)}
                  className="hidden"
                  id="modified-file"
                />
                <Button
                  onClick={() => document.getElementById('modified-file')?.click()}
                  size="sm"
                  variant="outline"
                >
                  <Upload className="w-3 h-3 mr-1" />
                  {t('common.upload')}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={modifiedText}
              onChange={(e) => setModifiedText(e.target.value)}
              placeholder={t('tools.textDiff.enterModifiedText')}
              className="min-h-[300px] font-mono text-sm"
            />
          </CardContent>
        </Card>
      </div>

      {/* Statistics */}
      {diffLines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.textDiff.comparisonStatistics')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats.added}
                </div>
                <div className="text-sm text-green-600 dark:text-green-400">{t('tools.textDiff.addedLines')}</div>
              </div>
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {stats.removed}
                </div>
                <div className="text-sm text-red-600 dark:text-red-400">{t('tools.textDiff.removedLines')}</div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                  {stats.unchanged}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.textDiff.unchangedLines')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Diff Result */}
      {diffLines.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.textDiff.differences')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-h-96 overflow-auto">
              <div className="font-mono text-sm space-y-1">
                {diffLines.map((line, index) => (
                  <div
                    key={index}
                    className={`flex ${
                      line.type === 'added'
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                        : line.type === 'removed'
                        ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <span className="w-8 text-center text-xs text-gray-500 mr-2">
                      {line.lineNumber}
                    </span>
                    <span className="w-4 text-center mr-2">
                      {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
                    </span>
                    <span className="flex-1">{line.content || ' '}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
